import { ArrowRight } from "lucide-react";
import { useMemo, useState, type JSX } from "react";
import { Link } from "react-router";

import { cn } from "../../lib/cn.js";
import { Switch } from "../ui/switch.js";

export interface DistributionEntry {
  key: string;
  label: string;
  value: number;
}

export interface DistributionBarsProps {
  /** Title for this distribution section (e.g. "By status"). Optional. */
  title?: string;
  /** How the title renders: a small uppercase caption (default) or a card
   *  heading that sits on the same row as the hide toggle. */
  titleVariant?: "label" | "heading";
  /** Entries to display. Order is preserved. */
  entries: DistributionEntry[];
  /**
   * Where a row should link. Breakdowns are where users form the intent to
   * drill in, so without this the most natural next click is a dead end.
   * Return null to leave a specific row inert.
   */
  hrefFor?: (entry: DistributionEntry) => string | null;
  /**
   * Tailwind background class for a row's bar. Defaults to a flat blue for
   * every row, which renders "Critical" and "Resolved" identically — pass
   * this to carry severity/status meaning in the colour.
   */
  colorFor?: (entry: DistributionEntry) => string;
  /**
   * Show each row's share of the total beside its count. Bars are scaled to
   * the largest entry (so the shape reads well), which means "35 of 40" and
   * "35 of 4000" look identical without this.
   */
  showShare?: boolean;
  /** Human-readable label override, e.g. mapping `oos` -> "Out of stock". */
  formatEntryLabel?: (entry: DistributionEntry) => string;
  /** Hard cap on the number of rows (never expandable). */
  limit?: number;
  /** Soft cap: show this many rows with a "Show all N" toggle for the rest.
   *  Ignored when `limit` is set. */
  initialVisible?: number;
  /** Show as small inline list (smaller bar). */
  dense?: boolean;
  /** When true, treats empty entries as a "no data" state instead of rendering nothing. */
  emptyMessage?: string;
  /**
   * Entry keys/labels (matched case-insensitively against either) that can be
   * toggled out of the view — e.g. `["Uncategorized"]`. Renders a switch when
   * at least one matching entry is present; hiding rebases nothing else.
   */
  hideableKeys?: string[];
  /** Label for the hide toggle (defaults to "Hide"). */
  hideableLabel?: string;
}

function formatLabel(raw: string): string {
  return raw
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function DistributionBars({
  title,
  titleVariant = "label",
  entries,
  hrefFor,
  colorFor,
  showShare = false,
  formatEntryLabel,
  limit,
  initialVisible,
  dense,
  emptyMessage,
  hideableKeys,
  hideableLabel = "Hide",
}: DistributionBarsProps): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const [hideOn, setHideOn] = useState(false);

  const hideSet = useMemo(
    () => new Set((hideableKeys ?? []).map((k) => k.trim().toLowerCase())),
    [hideableKeys],
  );
  const matchesHideable = (e: { key: string; label: string }): boolean =>
    hideSet.has(e.key.trim().toLowerCase()) || hideSet.has(e.label.trim().toLowerCase());
  const hasHideable = hideSet.size > 0 && entries.some(matchesHideable);
  const sourceEntries = hideOn ? entries.filter((e) => !matchesHideable(e)) : entries;

  // A hard `limit` wins; otherwise `initialVisible` collapses the tail behind a
  // toggle. Scaling always uses the full set's max so bar widths stay stable
  // whether the list is expanded or not.
  const capped = limit ? sourceEntries.slice(0, limit) : sourceEntries;
  const collapsible = !limit && initialVisible != null && capped.length > initialVisible;
  const trimmed = collapsible && !expanded ? capped.slice(0, initialVisible) : capped;
  const max = capped.reduce((m, e) => (e.value > m ? e.value : m), 0);
  // Share is against the visible set's total, not the max — so the percentages
  // in a column sum to 100 and mean what a reader expects.
  const total = capped.reduce((s, e) => s + e.value, 0);
  const hiddenCount = capped.length - trimmed.length;

  return (
    <div className="flex flex-col gap-2">
      {title || hasHideable ? (
        <div className="flex items-center justify-between gap-2">
          {title ? (
            titleVariant === "heading" ? (
              <h2 className="text-base font-semibold text-foreground">{title}</h2>
            ) : (
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {title}
              </div>
            )
          ) : (
            <span />
          )}
          {hasHideable ? (
            <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
              <span>{hideableLabel}</span>
              <Switch
                size="sm"
                checked={hideOn}
                onCheckedChange={setHideOn}
                aria-label={hideableLabel}
              />
            </label>
          ) : null}
        </div>
      ) : null}
      {trimmed.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          {emptyMessage ?? "No data"}
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {trimmed.map((entry) => {
            const pct = max > 0 ? Math.round((entry.value / max) * 100) : 0;
            const share = total > 0 ? (entry.value / total) * 100 : 0;
            const label = formatEntryLabel ? formatEntryLabel(entry) : formatLabel(entry.label);
            const href = hrefFor?.(entry) ?? null;
            const bar = colorFor?.(entry) ?? "bg-blue-500";

            const body = (
              <>
                <span className="truncate text-xs text-foreground" title={label}>
                  {label}
                </span>
                <div
                  className={cn(
                    "relative overflow-hidden rounded-full bg-muted",
                    dense ? "h-1.5" : "h-2",
                  )}
                  role="progressbar"
                  aria-valuenow={entry.value}
                  aria-valuemin={0}
                  aria-valuemax={max || 1}
                  aria-label={`${label}: ${entry.value}`}
                >
                  <div
                    className={cn("absolute inset-y-0 left-0 rounded-full transition-all", bar)}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="min-w-[2ch] text-right text-xs font-medium tabular-nums text-foreground">
                  {entry.value.toLocaleString()}
                </span>
                {showShare ? (
                  <span
                    className="min-w-[3.5ch] text-right text-[11px] tabular-nums text-muted-foreground"
                    title={`${share.toFixed(1)}% of ${total.toLocaleString()}`}
                  >
                    {share.toFixed(0)}%
                  </span>
                ) : null}
                {href ? (
                  <ArrowRight
                    className="h-3.5 w-3.5 shrink-0 text-muted-foreground/0 transition-all group-hover:translate-x-0.5 group-hover:text-muted-foreground"
                    aria-hidden
                  />
                ) : null}
              </>
            );

            const gridCols = cn(
              "grid items-center gap-2",
              "grid-cols-[minmax(6rem,8rem)_1fr_auto]",
              showShare && "grid-cols-[minmax(6rem,8rem)_1fr_auto_auto]",
              showShare && href && "grid-cols-[minmax(6rem,8rem)_1fr_auto_auto_auto]",
              !showShare && href && "grid-cols-[minmax(6rem,8rem)_1fr_auto_auto]",
            );

            return (
              <li key={entry.key}>
                {href ? (
                  <Link
                    to={href}
                    className={cn(
                      gridCols,
                      "group -mx-1.5 rounded-md px-1.5 py-1 transition-colors hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none",
                    )}
                  >
                    {body}
                  </Link>
                ) : (
                  <div className={gridCols}>{body}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {collapsible ? (
        <button
          type="button"
          className="self-start text-xs font-medium text-primary hover:underline"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Show less" : `Show all ${capped.length}`}
          {!expanded && hiddenCount > 0 ? ` (+${hiddenCount})` : ""}
        </button>
      ) : null}
    </div>
  );
}

/** Helper: convert a `Record<string, number>` to ordered entries (desc by value). */
export function recordToEntries(
  record: Record<string, number> | undefined | null,
): Array<{ key: string; label: string; value: number }> {
  if (!record) return [];
  return Object.entries(record)
    .map(([key, value]) => ({ key, label: key, value }))
    .sort((a, b) => b.value - a.value);
}
